/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-21 (original work) Open Assessment Technologies SA ;
 */

import { __ } from '@oat-sa-private/ui-core';
import mimeTypeDefs from '@oat-sa/tao-core-sdk/src/core/mimetype/definitions.js';

export const unresolvableMimeTypes = [
    'application/octet-stream',
    'application/base64',
    'multipart/related',
    'application/vnd.sealed'
];

// wildcard keys and strings, not actually used here, but needed so that their translations will be picked up
export const wildCardMimeTypes = {
    'image/*': __('all images'),
    'video/*': __('all videos'),
    'audio/*': __('all audios')
};

/**
 * Extends a mimeType to account for missing equivalent types
 * @param {string} mimeType
 * @returns {string|string[]} - array of valid mime types and extensions
 */
export const mimeToMimeWithEquivalents = (mimeType = '') => {
    const type = mimeType.trim().toLowerCase();
    if (type.length) {
        const mimeTypesList = mimeTypeDefs.getList(__);
        const match = mimeTypesList.find(entry => entry.mime === type);
        if (match && match.equivalent) {
            return [type].concat(match.equivalent);
        }
    }
    return type;
};

/**
 * Converts from mimeType string to accepted file extensions, if possible
 * @param {string} mimeType
 * @returns {string|string[]} - original string, label, or array of extensions
 */
export const mimeToExtensionsLabel = (mimeType = '') => {
    const type = mimeType.trim().toLowerCase();
    if (unresolvableMimeTypes.includes(type)) {
        return '';

    } else if (type.length) {
        const mimeTypesList = mimeTypeDefs.getList(__);
        const match = mimeTypesList.find(entry => entry.mime === type);
        if (match) {
            return match.extensionsLabels || match.label || '';
        }
    }
    return type;
};

/**
 * Manipulate string list with a given mapper function
 * @param {string} list - comma separated list
 * @param {function} mapper - a function taking a string, and returning a string or list of strings
 * @param {string} [glue] - separator string for rebuilding returned string
 * @returns {string} comma separated cleaned list of unique strings
 */
const transformTokenList = (list = [], mapper, glue = ', ') => (
    list
        .split(/\s+/)
        .map(mapper)
        .reduce((a, b) => a.concat(b), []) // flatten it
        .filter(elem => elem && elem.length > 0) // reject falsy & empty values
        .filter((elem, index, array) => array.indexOf(elem) === index) // dedupe final list
        .join(glue)
);

/**
 * Extend mime types to make comprehensive 'accept' attribute of <input>
 * @param {string} mimeTypes - space separated mime types
 * @returns {string} comma separated formats
 */
export const convertMimeTypesToAcceptAttr = mimeTypes => (
    transformTokenList(mimeTypes, mimeToMimeWithEquivalents, ',')
);

/**
 * Convert mime types to user friendly format
 * @param {string} mimeTypes - space separated mime types
 * @returns {string} comma separated formats
 */
export const convertMimeTypesToDisplayFormat = mimeTypes => (
    transformTokenList(mimeTypes, mimeToExtensionsLabel)
);

/**
 * Check the type matches with validTypes
 * @param {string} type - mime type
 * @param {string} validTypes - comma separated mime types and file extensions
 * @returns {Boolean}
 */
export const isValidType = (type, validTypes) => {
    if (!validTypes) {
        return true;
    }
    const starType = type.replace(/\/.*$/, '/*'); // for example image/png -> image/*
    const types = validTypes
        .split(',')
        .map(validType => validType.trim());

    return types.includes(type) || types.includes(starType);
};

/**
 * Check if the extension is in a list of valid extensions derived from mime types
 * @param {string} ext - file extension
 * @param {string} validTypes - comma separated mime types and file extensions
 * @returns {Boolean}
 */
export const hasValidExtension = (ext, validTypes) => {
    if (!validTypes) {
        return true;
    }
    const validExtensions = validTypes
        .split(',')
        .map(type => type.trim())
        .filter(type => type.startsWith('.'));

    return validExtensions.includes(`.${ext}`);
};

/**
 * Determine the MIME type based on file extension
 * @param {string} extension - File extension
 * @returns {string} - MIME type
 */
export const determineMimeType = (extension) => {
    const mimeList = mimeTypeDefs.getList(__);

    for (const mimeObj of mimeList) {
        if (mimeObj.extensionsLabels && mimeObj.extensionsLabels.includes(`.${extension}`)) {
            return mimeObj.mime;
        }

        if (mimeObj.equivalent && mimeObj.equivalent.includes(`.${extension}`)) {
            return mimeObj.mime;
        }
    }

    return 'application/octet-stream';
};
